import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { controlKeys, platformUsersKeys, remediationKeys } from "../queryKeys";
import useAPI from "./useAPI";

const useAssignUserManagement = () => {
  const { getAllPlatformUsers, assignControlAndRemediation, trackEvent } =
    useAPI();
  const queryClient = useQueryClient();
  const [loadingControlRemediationId, setLoadingControlRemediationId] =
    useState(null);

  const { data: platformUsersResponse, isLoading: usersLoading } = useQuery({
    queryKey: platformUsersKeys.all,
    queryFn: getAllPlatformUsers,
  });

  const platformUsers = useMemo(
    () =>
      platformUsersResponse?.data?.filter(
        (user) => user?.is_visible !== false
      ) || [],
    [platformUsersResponse]
  );

  const usersMap = useMemo(() => {
    const map = new Map();
    platformUsers.forEach((user) => {
      if (user.email) {
        map.set(user.email, user);
      }
    });
    return map;
  }, [platformUsers]);

  const handleAssignControlAndRemediation = async (
    controlRemediationId,
    userEmail
  ) => {
    setLoadingControlRemediationId(controlRemediationId);

    try {
      await assignControlAndRemediation(controlRemediationId, userEmail);
      queryClient.invalidateQueries({
        queryKey: controlKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: remediationKeys.all,
      });
    } finally {
      trackEvent(
        userEmail
          ? `User assigned control and remediation ${controlRemediationId} to ${userEmail}`
          : `User unassigned control and remediation ${controlRemediationId}`
      );
      setLoadingControlRemediationId(null);
    }
  };

  const isControlRemediationLoading = (controlRemediationId) =>
    loadingControlRemediationId === controlRemediationId;

  return {
    platformUsers,
    usersMap,
    usersLoading,
    handleAssignControlAndRemediation,
    loadingControlRemediationId,
    isControlRemediationLoading,
  };
};

export default useAssignUserManagement;
